import dayjs, { type Dayjs } from 'dayjs';
import { round } from 'lodash';
import { useMemo, useState } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';

type WeatherEntry = {
    tempMax: number;
    tempMin: number;
    date: string;
    gduAccum: number;
    precipAccum: number;
    otherData: {
        PrecipAmountLocalDayAccum: number;
        TemperatureLocalDayAvg: number;
    };
};

type GroupedWeather = Record<string, WeatherEntry[]>;

function groupWeatherData(data: WeatherEntry[], groupBy: 'day' | 'month', from?: string, to?: string): GroupedWeather {
    return data.reduce((acc: GroupedWeather, entry) => {
        const entryDate = dayjs(entry.date.split('T')[0]);

        if ((from && entryDate.isBefore(dayjs(from))) || (to && entryDate.isAfter(dayjs(to)))) {
            return acc; // Skip entries outside the date range
        }
        const key = entryDate.format(groupBy === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM');
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(entry);
        return acc;
    }, {});
}

const getFieldStage = (
    stages,
    {
        vegetativeStage,
        phenology,
    }: {
        vegetativeStage?: number;
        phenology?: string;
    }
) => {
    return stages?.find((stage) => {
        if (vegetativeStage && phenology)
            return stage.vegetativeStage === vegetativeStage && stage.phenology === phenology;
        if (vegetativeStage) return stage.vegetativeStage === vegetativeStage;
        if (phenology) return stage.phenology === phenology;
    });
};
export type FilterTypes = 'todas' | 'siembra' | 'v3-v6' | 'floracion' | 'madurez' | 'cosecha';

const useReportGraphics = (cropMonitoring, historicalWeather, unitsProp) => {
    const units = useUnitProvider(unitsProp);
    const cropData = getCropMonitoringData(cropMonitoring);
    const filters = useMemo<
        Record<
            string,
            {
                name: string;
                from: Dayjs | null;
                to: Dayjs | null;
                group: 'day' | 'month';
            }
        >
    >(() => {
        const allFilters = {
            todas: {
                name: _('Todas'),
                from: null,
                to: null,
                group: 'month' as const,
            },
            siembra: {
                name: _('Siembra'),
                from: cropMonitoring?.plantingDate,
                to: getFieldStage(cropData?.stages, {
                    phenology: 'emergence',
                })?.date,
                group: 'day' as const,
            },
            'v3-v6': {
                name: _('v3-v6'),
                from: getFieldStage(cropData?.stages, {
                    vegetativeStage: 3,
                })?.date,
                to: getFieldStage(cropData?.stages, {
                    vegetativeStage: 6,
                })?.date,
                group: 'day' as const,
            },
            floracion: {
                name: _('Floración'),
                from: getFieldStage(cropData?.stages, {
                    phenology: 'emergence',
                })?.date,
                to: getFieldStage(cropData?.stages, {
                    phenology: 'flowering',
                })?.date,
                group: 'day' as const,
            },
            madurez: {
                name: _('Madurez'),
                from: getFieldStage(cropData?.stages, {
                    phenology: 'flowering',
                })?.date,
                to: getFieldStage(cropData?.stages, {
                    phenology: 'maturity',
                })?.date,
                group: 'day' as const,
            },
            cosecha: {
                name: _('Cosecha'),
                from: getFieldStage(cropData?.stages, {
                    phenology: 'maturity',
                })?.date,
                to: getFieldStage(cropData?.stages, {
                    phenology: 'harvest',
                })?.date,
                group: 'day' as const,
            },
        };

        return Object.fromEntries(
            Object.entries(allFilters).filter(
                ([key, value]) => key === 'todas' || (Boolean(value.from) && Boolean(value.to))
            )
        );
    }, [cropMonitoring, cropData?.stages]);
    const [filter, setFilter] = useState<FilterTypes>('todas');

    const groupedWeather = useMemo(() => {
        const { from, to, group: groupBy } = filters[filter] || {};
        const grouped = groupWeatherData(historicalWeather?.results?.toReversed() || [], groupBy, from, to);
        return Object.entries(grouped).map(([key, entries]) => {
            const tempMax = Math.max(...entries.map((entry) => entry.tempMax));
            const tempMin = Math.min(...entries.map((entry) => entry.tempMin));
            const tempAvg =
                entries.reduce((sum, entry) => sum + Number(entry.otherData.TemperatureLocalDayAvg), 0) /
                entries.length;
            const gduAccum = entries.reduce((sum, entry) => sum + entry.gduAccum, 0);
            const precipAccum = entries.reduce((sum, entry) => sum + entry.otherData.PrecipAmountLocalDayAccum, 0);
            const frostRisk = units.fromTemperature(tempMin, 2) <= 3 || undefined;

            return {
                date: key,
                entries,
                tempMax: round(tempMax, 0),
                tempMin: round(tempMin, 0),
                tempAvg: round(tempAvg, 0),
                gduAccum,
                precipAccum: round(precipAccum, 2),
                frostRisk,
            };
        });
    }, [historicalWeather?.results, filter, filters, units]);

    return {
        filters,
        filter,
        setFilter,
        data: groupedWeather,
    };
};

export default useReportGraphics;
